import { normalizeDigitalProductPassport } from "./remanufacturing/buildRemanufacturedProductPassport";
import { normalizeServiceDigitalProductPassport } from "./remanufacturing/buildServiceDigitalProductPassport";
import { fromApiOrder } from "./remanufacturing/remanufacturingMilestoneAdapter";
import { serviceWorkflowFromInterestMessage } from "./serviceEoiMessage";
import { normalizeServiceWorkflow } from "./serviceOrderWorkflow";

/**
 * Normalizes marketplace order payloads from the API (snake_case → camelCase).
 * @param {unknown} raw
 * @returns {Record<string, unknown>|null}
 */
export function normalizeMarketplaceOrder(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return null;
  }

  const record = /** @type {Record<string, unknown>} */ (raw);
  const serviceMilestonesRaw =
    record.serviceMilestones ?? record.service_milestones ?? null;
  const serviceWorkflowRaw =
    record.serviceWorkflow ?? record.service_workflow ?? null;
  const normalizedServiceWorkflow =
    normalizeServiceWorkflow(serviceWorkflowRaw) ??
    serviceWorkflowFromInterestMessage(record.message);
  const listingClass = record.listingClass ?? record.listing_class ?? null;

  const normalizedMilestones =
    serviceMilestonesRaw != null
      ? fromApiOrder({
          listingClass,
          serviceWorkflow: normalizedServiceWorkflow,
          serviceMilestones: serviceMilestonesRaw,
        })
      : null;

  const productId = record.productId ?? record.product_id;
  const productName = record.productName ?? record.product_name;
  const digitalProductPassportRaw =
    record.digitalProductPassport ?? record.digital_product_passport ?? null;
  const normalizedPassport = normalizeDigitalProductPassport(
    digitalProductPassportRaw
  );
  const serviceDigitalProductPassportRaw =
    record.serviceDigitalProductPassport ??
    record.service_digital_product_passport ??
    null;
  const normalizedServicePassport = normalizeServiceDigitalProductPassport(
    serviceDigitalProductPassportRaw
  );

  const dashboardDismissedByCustomerAt =
    record.dashboardDismissedByCustomerAt ??
    record.dashboard_dismissed_by_customer_at ??
    null;
  const dashboardDismissedByProviderAt =
    record.dashboardDismissedByProviderAt ??
    record.dashboard_dismissed_by_provider_at ??
    null;

  return {
    ...record,
    ...(productId != null ? { productId } : {}),
    ...(productName != null ? { productName } : {}),
    listingClass: record.listingClass ?? record.listing_class ?? null,
    ...(dashboardDismissedByCustomerAt
      ? { dashboardDismissedByCustomerAt: String(dashboardDismissedByCustomerAt) }
      : {}),
    ...(dashboardDismissedByProviderAt
      ? { dashboardDismissedByProviderAt: String(dashboardDismissedByProviderAt) }
      : {}),
    ...(normalizedMilestones != null
      ? { serviceMilestones: normalizedMilestones }
      : {}),
    ...(normalizedPassport != null
      ? { digitalProductPassport: normalizedPassport }
      : {}),
    ...(normalizedServicePassport != null
      ? { serviceDigitalProductPassport: normalizedServicePassport }
      : {}),
    ...(normalizedServiceWorkflow != null
      ? { serviceWorkflow: normalizedServiceWorkflow }
      : {}),
  };
}

/**
 * Prefer the live product listing name over a stale snapshot on the order row.
 * @param {Record<string, unknown>} order
 * @param {Record<string, unknown>|null|undefined} product
 */
export function enrichOrderWithProduct(order, product) {
  const resolvedName =
    typeof product?.name === "string"
      ? product.name.trim()
      : typeof product?.productName === "string"
        ? product.productName.trim()
        : "";

  if (!resolvedName) {
    return order;
  }

  return { ...order, productName: resolvedName };
}

/**
 * @param {unknown} data
 * @returns {{ orders: Record<string, unknown>[], total: number }}
 */
export function normalizeMarketplaceOrderCollection(data) {
  if (Array.isArray(data)) {
    const orders = data
      .map((row) => normalizeMarketplaceOrder(row))
      .filter(Boolean);
    return { orders, total: orders.length };
  }

  if (data && typeof data === "object") {
    const record = /** @type {Record<string, unknown>} */ (data);

    if (Array.isArray(record.content)) {
      const orders = record.content
        .map((row) => normalizeMarketplaceOrder(row))
        .filter(Boolean);
      return {
        orders,
        total:
          typeof record.totalElements === "number"
            ? record.totalElements
            : orders.length,
      };
    }

    if (Array.isArray(record.orders)) {
      const orders = record.orders
        .map((row) => normalizeMarketplaceOrder(row))
        .filter(Boolean);
      return {
        orders,
        total: typeof record.total === "number" ? record.total : orders.length,
      };
    }
  }

  return { orders: [], total: 0 };
}
